import { fetchUtils, HttpError, type DataProvider } from 'react-admin';
import { getToken } from './authProvider';

/**
 * Maps react-admin's data provider contract onto the admin API.
 *
 * Written by hand rather than pulled from ra-data-simple-rest because the API
 * is shaped for the storefront to adopt later, not for react-admin: it returns
 * `{ data, total }` in the body instead of a Content-Range header, and several
 * screens are actions ("fulfil this order") rather than record updates.
 */
const apiUrl = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3000';

const httpClient = async (url: string, options: fetchUtils.Options = {}) => {
  const headers = new Headers((options.headers as Headers) ?? { Accept: 'application/json' });

  const token = getToken();
  if (token != null) headers.set('Authorization', `Bearer ${token}`);

  const response = await fetchUtils.fetchJson(url, { ...options, headers });

  // The API answers with JSON or with nothing. Anything else - in practice an
  // HTML page served in its place - is not an answer: fetchJson would pass it
  // on as `undefined`, and a screen waiting for data would wait forever.
  if (response.body !== '' && response.json === undefined) {
    throw new HttpError('Server vrátil neočakávanú odpoveď.', response.status, response.body);
  }

  return response;
};

interface ListResponse {
  readonly data: readonly unknown[];
  readonly total: number;
}

export const dataProvider: DataProvider = {
  getList: async (resource, params) => {
    const { page, perPage } = params.pagination ?? { page: 1, perPage: 25 };
    const { field, order } = params.sort ?? { field: 'id', order: 'DESC' };

    const query = new URLSearchParams({
      _page: String(page),
      _perPage: String(perPage),
      _sortField: field,
      _sortOrder: order.toLowerCase()
    });

    for (const [key, value] of Object.entries(params.filter ?? {})) {
      if (value == null || value === '') continue;
      query.set(key, String(value));
    }

    const { json } = await httpClient(`${apiUrl}/v1/${resource}?${query.toString()}`);
    const body = json as ListResponse;
    return { data: body.data as never[], total: body.total };
  },

  getOne: async (resource, params) => {
    const { json } = await httpClient(`${apiUrl}/v1/${resource}/${params.id}`);
    return { data: json };
  },

  getMany: async (resource, params) => {
    const query = new URLSearchParams({ ids: params.ids.join(','), _perPage: String(params.ids.length) });
    const { json } = await httpClient(`${apiUrl}/v1/${resource}?${query.toString()}`);
    return { data: (json as ListResponse).data as never[] };
  },

  getManyReference: async (resource, params) => {
    const query = new URLSearchParams({ [params.target]: String(params.id) });
    const { json } = await httpClient(`${apiUrl}/v1/${resource}?${query.toString()}`);
    const body = json as ListResponse;
    return { data: body.data as never[], total: body.total };
  },

  update: async (resource, params) => {
    const { json } = await httpClient(`${apiUrl}/v1/${resource}/${params.id}`, {
      method: 'PATCH',
      body: JSON.stringify(params.data)
    });
    return { data: { ...params.data, ...(json as object), id: params.id } as never };
  },

  updateMany: async () => {
    // Nothing in these screens updates in bulk, and a silent partial success
    // across a price change is not something to invent support for.
    throw new Error('Bulk updates are not supported by this API.');
  },

  create: async (resource, params) => {
    const { json } = await httpClient(`${apiUrl}/v1/${resource}`, {
      method: 'POST',
      body: JSON.stringify(params.data)
    });
    return { data: json as never };
  },

  delete: async (resource, params) => {
    const { json } = await httpClient(`${apiUrl}/v1/${resource}/${params.id}`, { method: 'DELETE' });
    return { data: json as never };
  },

  deleteMany: async () => {
    throw new Error('Bulk deletes are not supported by this API.');
  }
};

/** Calls an action endpoint that is not a record update, e.g. fulfilling an order. */
export async function callAction(
  path: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST',
  body?: unknown
) {
  const { json } = await httpClient(`${apiUrl}/v1/${path}`, {
    method,
    ...(body == null ? {} : { body: JSON.stringify(body) })
  });
  return json;
}
